import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InstitutionTypeMultiSelectControl } from '@/module/Nomen/InstitutionType/InstitutionTypeMultiSelectControl';
import { PatentMembershipSelectControl } from '@/module/Nomen/PatentMembership/PatentMembershipSelectControl';
import { PatentOwnerSelectControl } from '@/module/Nomen/PatentOwner/PatentOwnerSelectControl';
import { PatentStateSelectControl } from '@/module/Nomen/PatentState/PatentStateSelectControl';
import { PatentTypeSelectControl } from '@/module/Nomen/PatentType/PatentTypeSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, NumberInputControl, TextInputControl } from '@/core/components';

import { PatentSchema } from './model/PatentSchema';

interface PatentFormProps extends FormSubmit<PatentSchema> {
  title: string;
}

export function PatentForm(props: PatentFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<PatentSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<PatentSchema>
              withAsterisk
              name="title"
              label={t('patents.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Group>
              <PatentTypeSelectControl<PatentSchema>
                withAsterisk
                name="type"
                label={t('patents.form.fields.type')}
                isSubmitting={isSubmitting}
              />
              <PatentStateSelectControl<PatentSchema>
                withAsterisk
                name="state"
                label={t('patents.form.fields.state')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <Group>
              <TextInputControl<PatentSchema>
                withAsterisk
                name="registrationNo"
                label={t('patents.form.fields.registrationNumber')}
                isSubmitting={isSubmitting}
              />
              <PatentOwnerSelectControl<PatentSchema>
                withAsterisk
                name="owner"
                label={t('patents.form.fields.owner')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <Group>
              <NumberInputControl<PatentSchema>
                name="submissionYear"
                label={t('patents.form.fields.submissionYear')}
                isSubmitting={isSubmitting}
                min={1997}
                max={new Date().getFullYear()}
                allowDecimal={false}
                withAsterisk
              />
              <NumberInputControl<PatentSchema>
                name="grantingYear"
                label={t('patents.form.fields.grantingYear')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
                min={1997}
                max={new Date().getFullYear()}
              />
            </Group>
            <NumberInputControl<PatentSchema>
              name="universityAffiliatedNoOfAuthors"
              label={t('patents.form.fields.universityAffiliatedNoOfAuthors')}
              description={t('patents.form.fields.universityAffiliatedNoOfAuthorsDescription')}
              isSubmitting={isSubmitting}
              allowDecimal={false}
            />
          </Content>
          <Content type="full">
            <InstitutionTypeMultiSelectControl<PatentSchema>
              name="institutions"
              label={t('patents.form.fields.institution')}
              isSubmitting={isSubmitting}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<PatentSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <PatentMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
